import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

import { cn } from "@/utils/cn";
import { LayoutBackground } from "./ui/LayoutBackground";

/** Image keys are static; copy is built per render so it follows the language. */
const SLIDE_IMAGES = [
  "illust-emp-management.svg",
  "illust-payroll-management.svg",
  "illust-time-attendance.svg",
] as const;

const buildSlides = (
  t: (key: string) => string,
): Array<{ heading: string; sub: string; image: string }> => [
  {
    image: SLIDE_IMAGES[0],
    heading: t("slides.employee_management"),
    sub: t("slides.employee_management_sub"),
  },
  {
    image: SLIDE_IMAGES[1],
    heading: t("slides.payroll_management"),
    sub: t("slides.payroll_management_sub"),
  },
  {
    image: SLIDE_IMAGES[2],
    heading: t("slides.time_attendance"),
    sub: t("slides.time_attendance_sub"),
  },
];

const SLIDE_INTERVAL = 4000; // 4 seconds
const slideVariants = {
  enter: { opacity: 0, x: 50 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -50 },
};

export const AuthSlider = () => {
  const { t } = useTranslation("auth");
  const Slides = buildSlides(t);
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentSlide = Slides[currentIndex];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % Slides.length);
    }, SLIDE_INTERVAL);
    return () => clearInterval(timer);
  }, []);

  return (
    <LayoutBackground>
      <div className="max-w-2xl mx-auto">
        <div className="max-h-120 bg-primary lg:p-8 lg:py-24 p-5 py-9 rounded-md overflow-hidden flex flex-col items-center justify-center">
          <motion.div
            exit="exit"
            initial="enter"
            animate="center"
            key={Slides[currentIndex].image}
            variants={slideVariants}
            transition={{ duration: 0.8, ease: "linear" }}
          >
            <img
              width={250}
              height={250}
              className="mx-auto"
              alt={currentSlide.heading}
              src={`/illustrations/${currentSlide.image}`}
            />
          </motion.div>
          <motion.div
            initial={{
              opacity: 0,
              y: 30,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            exit={{
              opacity: 0,
              y: 30,
            }}
            key={Slides[currentIndex].heading}
            transition={{ duration: 0.8, ease: "linear" }}
          >
            <div className="text-center mt-4">
              <h1 className="font-semibold text-2xl text-white">
                {currentSlide.heading}
              </h1>
              <p className="text-center text-white/70 max-w-md">
                {currentSlide.sub}
              </p>
            </div>
          </motion.div>
          {/* Pagination dots */}
          <div className="flex gap-2 mt-11 w-full justify-center">
            {Slides.map((_, i) => (
              <div
                key={i}
                role="button"
                tabIndex={0}
                className={cn(
                  "w-6 h-1 rounded-full cursor-pointer",
                  i === currentIndex ? "bg-border" : "bg-info"
                )}
                onClick={() => setCurrentIndex(i)}
                aria-label={t("slides.goto_slide", { number: i + 1 })}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setCurrentIndex(i);
                }}
              />
            ))}
          </div>
        </div>
        <p className="text-base text-white text-center mt-[66px] px-14 ">
          {t("slides.tagline")}
        </p>
      </div>
    </LayoutBackground>
  );
};
